import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { scrollTo } from "../lib/motion";
import { Logo } from "./Logo";
import { CALCOM_LINK } from "../config";

const LINKS = [
  { label: "Solutions", href: "#services" },
  { label: "AI Employees", href: "#employees" },
  { label: "Outcomes", href: "#cases" },
  { label: "About", href: "#why" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => { setOpen(false); scrollTo(href); };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 left-0 right-0 z-[100] px-5 md:px-10"
      data-testid="navbar"
    >
      <div
        className={`mx-auto mt-4 flex max-w-7xl items-center justify-between rounded-full px-5 py-3 transition-[background,box-shadow,border-color] duration-500 ${
          scrolled ? "glass glow-accent" : "border border-transparent"
        }`}
      >
        <button onClick={() => go("#top")} className="flex items-center" data-testid="logo-button" aria-label="SÛRSWA AI home">
          <Logo className="text-lg md:text-xl" />
        </button>

        <nav className="hidden items-center gap-8 md:flex">
          {LINKS.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className="text-sm text-[#8a99a8] transition-colors duration-300 hover:text-white"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          {CALCOM_LINK ? (
            <button
              data-cal-link={CALCOM_LINK}
              data-cal-config='{"layout":"month_view","theme":"dark"}'
              data-testid="nav-book-time"
              className="hidden text-sm text-[#8a99a8] transition-colors duration-300 hover:text-white md:block"
            >
              Book a time
            </button>
          ) : (
            <button
              onClick={() => go("#contact")}
              data-testid="nav-book-time"
              className="hidden text-sm text-[#8a99a8] transition-colors duration-300 hover:text-white md:block"
            >
              Book a time
            </button>
          )}
          <button
            onClick={() => go("#contact")}
            data-testid="nav-cta-button"
            className="hidden rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-[#070708] transition-transform duration-300 hover:scale-[1.04] active:scale-95 md:block"
          >
            Book a Strategy Session
          </button>
          <button className="md:hidden" onClick={() => setOpen((o) => !o)} data-testid="mobile-menu-toggle" aria-label="Menu">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="glass mx-auto mt-2 max-w-7xl rounded-2xl p-5 md:hidden"
            data-testid="mobile-menu"
          >
            {LINKS.map((l) => (
              <button key={l.href} onClick={() => go(l.href)} className="block w-full py-3 text-left text-base text-white/90">
                {l.label}
              </button>
            ))}
            {CALCOM_LINK ? (
              <button
                data-cal-link={CALCOM_LINK}
                data-cal-config='{"layout":"month_view","theme":"dark"}'
                onClick={() => setOpen(false)}
                className="block w-full py-3 text-left text-base text-white/90"
              >
                Book a time
              </button>
            ) : (
              <button onClick={() => go("#contact")} className="block w-full py-3 text-left text-base text-white/90">
                Book a time
              </button>
            )}
            <button onClick={() => go("#contact")} className="mt-3 w-full rounded-full bg-white py-3 text-sm font-semibold text-[#070708]">
              Book a Strategy Session
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
